/**
 * SignedOutByOtherTabDrawer
 *
 * Displays a drawer notification when the user was signed out by another tab.
 * Placed in RootLayout to appear on any page.
 */

import { useState } from 'react';
import { useAuth } from '@hooks/useAuth';
import { useTakeoverFeed } from '@hooks/useTakeoverFeed';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
} from '@components/ui/drawer';
import { Button } from '@components/ui/button';

export function SignedOutByOtherTabDrawer() {
  const { signIn, signedOutByOtherTabAtMount } = useAuth();
  // Open from the first render, from `useAuth().signedOutByOtherTabAtMount`; never touches
  // storage. State, not the context value itself, because the user can close it. Opening in an
  // effect instead left the first commit with the takeover bit unset, so a celebration intent
  // present at boot could be granted by CelebrationHost's passive mount effect before this
  // drawer parked the mutex (§6.4).
  const [isOpen, setIsOpen] = useState(signedOutByOtherTabAtMount);
  useTakeoverFeed('signed-out-other-tab', isOpen);

  const handleSignIn = () => {
    setIsOpen(false);
    signIn();
  };

  const handleContinueAsGuest = () => {
    setIsOpen(false);
  };

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>Signed out</DrawerTitle>
          <DrawerDescription>You were signed out from another browser tab.</DrawerDescription>
        </DrawerHeader>
        <DrawerFooter>
          <Button
            onClick={handleSignIn}
            className="bg-mb-yellow hover:bg-mb-yellow text-black font-semibold rounded-full px-8
          w-full h-12 shadow-glow-yellow transition-all duration-200"
          >
            Sign in again
          </Button>
          <Button variant="ghost" onClick={handleContinueAsGuest}>
            Continue as guest
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
